import React, { useEffect, useState, Fragment, useRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import styles from "./ExactForm.module.scss";
import { Box } from "@mui/material";
import moment from "moment";
import CustomSnackbar from "../../components/customSnackBar/CustomSnackbar";
import {
  exactSyncApiStatus,
  getExactSyncEligibility,
  postExactAuthCode,
  syncExactTableApi,
} from "../../services/APIService";
import _ from "lodash";

import { useApiErrorModalContext } from "../../errorHandling/ApiErrorModalContext";
import PropTypes from "prop-types";
import { EXACT_CLIENT_ID, EXACT_REDIRECTION_URL } from "../../config/appConfig";

export default function ExactTable(props) {
  const {
    code,
    allSync = false,
    setAllSync,
    fullRefresh,
    syncAllowed = false,
    onSyncPermissionChange,
    activeSyncLastSync,
  } = props;
  const linkClicked = useRef(false);
  const countdownIntervalRef = useRef(null);
  const countdownDeadlineRef = useRef(null);
  const countdownIsLocalSyncRef = useRef(false);
  const syncFeedbackPendingRef = useRef(false);

  const syncTypeList = {
    1: "Relaties",
    2: "Abonnementen",
    3: "Abonn. regels",
    4: "Items",
    0: "-",
  };

  const [disableSyncBtnFlag, setDisableSyncBtnFlag] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [initialConnectFlag, setInitialConnectFlag] = useState(false);
  const [syncTableData, setSyncTableData] = useState([]);
  const [syncCountdown, setSyncCountdown] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const { setApiErrorModalState } = useApiErrorModalContext();

  const stopSyncCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    countdownDeadlineRef.current = null;
    setSyncCountdown(null);
  };

  const updateSyncCountdown = (remainingSeconds, isLocalSync = true) => {
    const deadline = Date.now() + remainingSeconds * 1000;
    countdownDeadlineRef.current = deadline;
    countdownIsLocalSyncRef.current = isLocalSync;
    const updateRemaining = () => {
      if (countdownDeadlineRef.current === null) {
        return;
      }
      const remainingSeconds = Math.max(
        0,
        Math.ceil((countdownDeadlineRef.current - Date.now()) / 1000)
      );
      setSyncCountdown(remainingSeconds);
      if (remainingSeconds === 0) {
        if (!countdownIsLocalSyncRef.current) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return;
        }
        finishSyncFeedback(false, "Exact sync timed out");
        setAllSync(false);
      }
    };

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    updateRemaining();
    if (deadline > Date.now()) {
      countdownIntervalRef.current = setInterval(updateRemaining, 1000);
    }
  };

  const finishSyncFeedback = (success, message) => {
    if (!syncFeedbackPendingRef.current) {
      return;
    }
    syncFeedbackPendingRef.current = false;
    stopSyncCountdown();
    setSyncResult({ message, success });
    checkSyncPermission();
  };

  const checkSyncPermission = async () => {
    try {
      const response = await getExactSyncEligibility();
      if (onSyncPermissionChange) {
        onSyncPermissionChange(response);
      }
      if (response?.reason === "invalid-token") {
        setInitialConnectFlag(true);
      }
      return response;
    } catch (error) {
      setApiErrorModalState({ error });
      return null;
    }
  };

  const handleAllSync = (type = 1) => {
    syncExactTableApi(type, fullRefresh).then((response) => {
      if (!syncFeedbackPendingRef.current) {
        return;
      }
      if (_.get(response, "success", true) === true) {
        if (type < 4) {
          handleAllSync(type + 1);
        } else {
          fetchSyncDetails();
          setAllSync(false);
          setDisableSyncBtnFlag(false);
        }
      } else {
        finishSyncFeedback(false, "Exact sync failed");
        if (_.get(response, "message", "") === "Unauthorized") {
          setInitialConnectFlag(false);
        } else {
          setApiErrorModalState({ error: response });
        }        
        setAllSync(false);
        setDisableSyncBtnFlag(false);
      }
    }).catch((error) => {
      if (!syncFeedbackPendingRef.current) {
        return;
      }
      finishSyncFeedback(false, "Exact sync failed");
      setAllSync(false);
      setDisableSyncBtnFlag(!syncAllowed);
      setApiErrorModalState({ error });
    });
  };

  function fetchSyncDetails() {
    exactSyncApiStatus({ tableType: 0 }).then((response) => {
      if (
        !_.isEmpty(_.get(response, "errorMessage", "")) ||
        _.get(response, "success", true) === false
      ) {
        _.set(response, "status", 200);
        finishSyncFeedback(false, "Exact sync failed");
        setIsLoading(false);
        setInitialConnectFlag(false);
        setApiErrorModalState({ error: response });
      } else {
        setIsLoading(false);
        setSyncTableData(response);
        setDisableSyncBtnFlag(!syncAllowed);
        finishSyncFeedback(true, "Exact sync completed");
      }
    }).catch((error) => {
      finishSyncFeedback(false, "Exact sync failed");
      setIsLoading(false);
      setDisableSyncBtnFlag(true);
      setApiErrorModalState({ error });
    });
  }

  const AuthApiCall = () => {
    postExactAuthCode({ code }).then((response) => {
      if (
        !_.isEmpty(_.get(response, "errorMessage", "")) ||
        _.get(response, "success", true) === false
      ) {
        _.set(response, "status", 200);
        setIsLoading(false);
        setInitialConnectFlag(true);
      } else {
        checkSyncPermission().then((permission) => {
          if (
            permission?.status === true ||
            permission?.reason === "active-sync"
          ) {
            setInitialConnectFlag(false);
          }
          setIsLoading(false);
        });
      }
    }).catch((error) => {
      setIsLoading(false);
      setInitialConnectFlag(true);
      setApiErrorModalState({ error });
    });
  };

  useEffect(() => {
    setIsLoading(true);
    if (code) {
      AuthApiCall();
    } else {
      setIsLoading(false);
    }
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!allSync) {
      setDisableSyncBtnFlag(!syncAllowed);
    }
  }, [syncAllowed, allSync, disableSyncBtnFlag]);

  useEffect(() => {
    if (!activeSyncLastSync || allSync) {
      return;
    }

    const lastSyncUtc = moment.utc(activeSyncLastSync);
    if (!lastSyncUtc.isValid()) {
      return;
    }

    const elapsedSeconds = Math.max(
      0,
      Math.floor(moment.utc().diff(lastSyncUtc, "seconds"))
    );
    const remainingSeconds = Math.max(0, 15 * 60 - elapsedSeconds);
    updateSyncCountdown(remainingSeconds, false);

    return () => {
      if (!countdownIsLocalSyncRef.current && countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        countdownDeadlineRef.current = null;
      }
    };
    // The timer is initialized from each new active-sync timestamp.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSyncLastSync, allSync]);

  useEffect(() => {
    if (allSync && !isLoading && !initialConnectFlag) {
      if (syncAllowed) {
        syncFeedbackPendingRef.current = true;
        setSyncResult(null);
        updateSyncCountdown(15 * 60);
        handleAllSync(1);
      } else {
        syncFeedbackPendingRef.current = false;
        stopSyncCountdown();
        setAllSync(false);
        setDisableSyncBtnFlag(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSync, isLoading, initialConnectFlag, syncAllowed]);

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const getDateInFormat = (updatedDate) => {
    if (!_.isNull(updatedDate)) {
      let date = moment(updatedDate).format("ll");
      let formatedTime = moment(updatedDate).format("HH:mm");
      return `${date} om ${formatedTime}`;
    } else {
      return "-";
    }
  };

  const handleLoginClick = (e) => {
    if (linkClicked.current) {
      e.preventDefault();
    } else {
      linkClicked.current = true;
    }
  };

  const getNoofDays = (date) => {
    if (!_.isNull(date)) {
      let day2 = moment(new Date());

      let day1 = moment(date);
      return day2.diff(day1, "days");
    } else {
      return "-";
    }
  };

  return (
    <Box data-testid="ExactTable">
      {syncCountdown !== null ? (
        <Box role="status" aria-live="polite">
          Exact-synchronisatie bezig. Resterende tijd:{" "}
          {String(Math.floor(syncCountdown / 60)).padStart(2, "0")}:
          {String(syncCountdown % 60).padStart(2, "0")}
        </Box>
      ) : null}
      <CustomSnackbar
        open={Boolean(syncResult)}
        message={syncResult?.message || ""}
        successMessage={syncResult?.success !== false}
        setOpen={(open) => {
          if (!open) {
            setSyncResult(null);
          }
        }}
      />
      {isLoading ? (
        <Box>Bezig met laden...</Box>
      ) : (
        <Fragment>
          {!initialConnectFlag ? (
            <Box className={`twoSectionPageWrapper`}>
              <Box
                className={`${styles.exactFormLeftWrapper} leftSideWraspper`}
              >
                <TableContainer
                  component={Paper}
                  className={styles.tableWrapper}
                >
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <b>Koppeling</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>Laatste nieuwe data</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>Aantal records </b>
                        </TableCell>

                        <TableCell align="left">
                          <b>
                            Dagen <sup className={styles.subStyles}>*</sup>
                          </b>
                        </TableCell>

                        <TableCell align="left">
                          <b>Laatste check</b>
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syncTableData.map((row) => (
                        <TableRow
                          key={row.type}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {syncTypeList[row.type]}
                          </TableCell>
                          <TableCell align="left">
                            {getDateInFormat(row.lastSync)}
                          </TableCell>
                          <TableCell align="left">{row.total_count}</TableCell>

                          <TableCell align="left">
                            <b>
                              <span
                                className={
                                  getNoofDays(row.lastUpdated) > 1
                                    ? "error"
                                    : ""
                                }
                              >
                                {getNoofDays(row.lastUpdated)}
                              </span>
                            </b>
                          </TableCell>

                          <TableCell align="left">
                            {getDateInFormat(row.lastUpdated)}
                          </TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box className={styles.bottomNote}>
                  <span className="error">*</span> Koppeling moet
                  gesynchroniseerd worden
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              <span className={styles.normalText}>
                Er ging iets fout, Log opnieuw in -{" "}
              </span>
              <a
                rel="noopener noreferrer"
                href={`https://start.exactonline.nl/api/oauth2/auth?client_id=${EXACT_CLIENT_ID}&redirect_uri=${EXACT_REDIRECTION_URL}&response_type=code`}
                target="framename"
                onClick={handleLoginClick}
              >
                <span className={styles.btnText}>Inloggen in Exact</span>
              </a>
            </Box>
          )}
        </Fragment>
      )}
    </Box>
  );
}

ExactTable.propTypes = {
  code: PropTypes.any,
  allSync: PropTypes.any,
  setAllSync: PropTypes.any,
  fullRefresh: PropTypes.any,
  syncAllowed: PropTypes.bool,
  onSyncPermissionChange: PropTypes.func,
  activeSyncLastSync: PropTypes.string,
};
